import {
  type ClipboardEvent,
  forwardRef,
  type KeyboardEvent,
  type RefObject,
  type TextareaHTMLAttributes,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';

import { cn } from '@/utils';

export type AutoGrowTextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  maxRows?: number;
  singleLine?: boolean;
  onSubmitShortcut?: () => void;
};

/* Textarea that grows to fit its content so long text wraps onto multiple lines */
export const AutoGrowTextarea = forwardRef<HTMLTextAreaElement, AutoGrowTextareaProps>(
  ({ maxRows, singleLine, onSubmitShortcut, onKeyDown, onPaste, className, value, ...props }, forwardedRef) => {
    const innerRef = useRef<HTMLTextAreaElement>(null);
    useImperativeHandle(forwardedRef, () => innerRef.current as HTMLTextAreaElement);
    useAutoGrow(innerRef, value, maxRows);

    const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (!e.nativeEvent.isComposing && e.key === 'Enter') {
        if ((e.metaKey || e.ctrlKey) && onSubmitShortcut) {
          e.preventDefault();
          onSubmitShortcut();
        } else if (singleLine) {
          e.preventDefault();
        }
      }
      onKeyDown?.(e);
    };

    // strip newlines from pasted content too.
    // execCommand keeps the cursor position and undo history intact
    const handlePaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
      if (singleLine) {
        const pasted = e.clipboardData.getData('text');
        if (/[\r\n]/.test(pasted)) {
          e.preventDefault();
          document.execCommand('insertText', false, pasted.replace(/[\r\n]+/g, ' '));
        }
      }
      onPaste?.(e);
    };

    return (
      <textarea
        ref={innerRef}
        rows={1}
        value={value}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        className={cn('resize-none overflow-hidden', className)}
        {...props}
      />
    );
  },
);

AutoGrowTextarea.displayName = 'AutoGrowTextarea';

/* Grows the textarea's height to fit its content */
const useAutoGrow = (
  ref: RefObject<HTMLTextAreaElement | null>,
  value: AutoGrowTextareaProps['value'],
  maxRows?: number,
) => {
  // biome-ignore lint/correctness/useExhaustiveDependencies: the height is derived from `value` through the DOM (scrollHeight), so `value` is the correct re-measure trigger even though it isn't read directly here.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    // a hidden textarea (`display: none`) measures 0, so keep `auto` and its natural height
    if (el.scrollHeight === 0) return;
    const cap = resolveMaxHeight(el, maxRows);
    const next = cap ? Math.min(el.scrollHeight, cap) : el.scrollHeight;
    el.style.height = `${next}px`;
    el.style.overflowY = cap && el.scrollHeight > cap ? 'auto' : 'hidden';
  }, [ref, value, maxRows]);
};

// Resolve the pixel cap, converting `maxRows` via the element's line-height.
const resolveMaxHeight = (el: HTMLTextAreaElement, maxRows?: number): number | undefined => {
  if (!maxRows) return undefined;
  const { lineHeight, fontSize } = getComputedStyle(el);
  const resolvedLineHeight = Number.parseFloat(lineHeight) || Number.parseFloat(fontSize) * 1.2;
  return resolvedLineHeight * maxRows;
};
